import { IDayType, ISelectedTicket, ITicketGroup } from "types/improved-booking"

// Updated Interfaces for clarity and to include overall failure message
export interface TicketTypeValidationStatus {
    ticketTypeId: string
    isIncreaseDisabled: boolean
    isDecreaseDisabled: boolean
    ticketTypeStatusMessage: string // Specific message for this ticket type
    ticketTypeStatusColor: string
}

export interface GroupValidationStatus {
    groupId: string
    groupStatusMessage: string // Specific message for this group
}

export interface OverallValidationResult {
    ticketTypeStatuses: Record<string, TicketTypeValidationStatus>
    groupStatuses: Record<string, GroupValidationStatus>
    isPass: boolean
    isFail: boolean
    failureReasonMessage?: string // General message for overall failure, takes precedence
}

/**
 * Modify the logic to also disable increments on normal tickets if waitlist tickets are chosen
 * and vice versa.
 */
export function calculateTicketAndGroupStatuses(
    tickets: IDayType[],
    selectedTickets: ISelectedTicket[],
    dynamicTranslate: (key: string, params?: Record<string, any>) => string,
    timeslotId?: string
): OverallValidationResult {
    let isPass = false
    let isFail = false
    let overallFailureReasonMessage: string | undefined = undefined

    const ticketTypeStatuses: Record<string, TicketTypeValidationStatus> = {}
    const groupStatuses: Record<string, GroupValidationStatus> = {}

    const ticketMap: Record<string, IDayType> = {}
    tickets.forEach((ticket) => {
        ticketMap[ticket.type_id] = ticket
    })

    let hasSelectedNormalTicket = false
    let hasSelectedWaitlistTicket = false

    selectedTickets.forEach((st) => {
        // Only consider current timeslot if provided
        if (timeslotId && st.timeslot_id !== timeslotId) return
        const ticket = ticketMap[st.type_id]
        if (ticket) {
            const isSoldOut =
                ticket.limit === 0 || ticket.limit < (ticket.min || 1)
            const isStartWaitlist =
                typeof ticket.wait_list === "number" &&
                ticket.wait_list > 0 &&
                isSoldOut

            if (isStartWaitlist) {
                hasSelectedWaitlistTicket = true
            } else {
                hasSelectedNormalTicket = true
            }
        }
    })

    const groupsToCheck: Record<
        string,
        {
            group: ITicketGroup
            tickets: IDayType[]
            totalSelectedCount: number
            selectedTicketTypes: Set<string>
        }
    > = {}

    const ticketTypeCounts: Record<string, number> = {}
    tickets.forEach((ticket) => {
        ticketTypeCounts[ticket.type_id] = 0
    })

    selectedTickets.forEach((selectedTicket) => {
        // Only count tickets that belong to the current timeslot when provided
        if (timeslotId && selectedTicket.timeslot_id !== timeslotId) return
        if (ticketTypeCounts[selectedTicket.type_id] !== undefined) {
            ticketTypeCounts[selectedTicket.type_id]++
        } else {
            // console.warn("[Debug bookingHelpers] Encountered selectedTicket.type_id not in available types during count:", selectedTicket.type_id, "Available type_ids for counting:", Object.keys(ticketTypeCounts));
        }
    })

    tickets.forEach((ticket) => {
        const groupId = ticket.group?._id
        if (groupId && ticket.group) {
            if (!groupsToCheck[groupId]) {
                groupsToCheck[groupId] = {
                    group: ticket.group,
                    tickets: [],
                    totalSelectedCount: 0,
                    selectedTicketTypes: new Set<string>(),
                }
            }
            groupsToCheck[groupId].tickets.push(ticket)
            const currentTicketCount = ticketTypeCounts[ticket.type_id] || 0
            if (currentTicketCount > 0) {
                groupsToCheck[groupId].totalSelectedCount += currentTicketCount
                groupsToCheck[groupId].selectedTicketTypes.add(ticket.type_id)
            }
        }
    })

    const isWaitlistTicket = (ticket: IDayType) => {
        const isSoldOut = ticket.limit === 0 || ticket.limit < (ticket.min || 1)
        return (
            typeof ticket.wait_list === "number" &&
            ticket.wait_list > 0 &&
            isSoldOut
        )
    }

    Object.values(groupsToCheck).forEach((groupData) => {
        const {
            group,
            tickets: groupTicketsList,
            totalSelectedCount,
            selectedTicketTypes,
        } = groupData

        // Apply group-level rules whenever there is at least one ticket
        // selected in this group and the group defines min and/or max.
        const hasMin = typeof group.min === "number"
        const hasMaxRule = typeof group.max === "number" && group.max > 0

        // Group MIN should only be enforced when the user mixes ticket types
        // from this group (at least two distinct ticket types selected).
        // When only a single type from the group is selected, we instead
        // enforce that ticket's own min rule.
        const groupMinActive = hasMin && selectedTicketTypes.size >= 2

        if (totalSelectedCount > 0 && (groupMinActive || hasMaxRule)) {
            const groupMin = hasMin ? group.min! : 0
            const groupMax = hasMaxRule ? group.max! : Infinity
            let currentGroupStatusMessage = ""
            const groupNameForMessage =
                group.en_name || group.ar_name || "This Group"

            if (groupMinActive && totalSelectedCount < groupMin) {
                currentGroupStatusMessage = dynamicTranslate(
                    "group_min_tickets_message",
                    {
                        groupMin,
                        groupName: groupNameForMessage,
                    }
                )
                isFail = true
            } else if (hasMaxRule && totalSelectedCount > groupMax) {
                currentGroupStatusMessage = dynamicTranslate(
                    "group_max_tickets_message",
                    {
                        groupMax,
                        groupName: groupNameForMessage,
                    }
                )
                isFail = true
            } else if (totalSelectedCount > 0) {
                isPass = true
            }
            groupStatuses[group._id] = {
                groupId: group._id,
                groupStatusMessage: currentGroupStatusMessage,
            }

            // For tickets in this group we always enforce per-ticket max.
            // We only enforce per-ticket min when the group MIN rule is not active.
            groupTicketsList.forEach((ticket) => {
                const ticketTypeId = ticket.type_id
                const currentTicketCountForThisType =
                    ticketTypeCounts[ticketTypeId] || 0
                const isStartWaitlist = isWaitlistTicket(ticket)
                const limitValue = isStartWaitlist
                    ? ticket.wait_list
                    : ticket.limit
                const maxAvailable =
                    ticket.max === 0
                        ? limitValue
                        : Math.min(ticket.max, limitValue!)
                let isIncreaseDisabled =
                    currentTicketCountForThisType >= maxAvailable!
                const isDecreaseDisabled = currentTicketCountForThisType === 0
                if (
                    hasSelectedNormalTicket &&
                    !hasSelectedWaitlistTicket &&
                    isStartWaitlist
                )
                    isIncreaseDisabled = true
                else if (
                    hasSelectedWaitlistTicket &&
                    !hasSelectedNormalTicket &&
                    !isStartWaitlist
                )
                    isIncreaseDisabled = true
                else if (hasSelectedNormalTicket && hasSelectedWaitlistTicket)
                    isIncreaseDisabled = true

                let msg = ""
                let color = ""
                const ticketNameForMessage =
                    ticket.en_title || ticket.ar_title || "This Ticket"

                const enforceTicketMin = !groupMinActive

                if (
                    enforceTicketMin &&
                    currentTicketCountForThisType > 0 &&
                    currentTicketCountForThisType < ticket.min
                ) {
                    msg = dynamicTranslate("select_min_tickets_for_type", {
                        min: ticket.min,
                        ticketName: ticketNameForMessage,
                    })
                    color = "red"
                    isFail = true
                } else if (
                    currentTicketCountForThisType > ticket.max &&
                    ticket.max !== 0
                ) {
                    msg = dynamicTranslate("select_max_tickets_for_type", {
                        max: ticket.max,
                        ticketName: ticketNameForMessage,
                    })
                    color = "red"
                    isFail = true
                } else if (
                    currentTicketCountForThisType > 0 &&
                    !groupMinActive
                ) {
                    // When group min is not active, a valid per-ticket
                    // selection should contribute to overall pass.
                    isPass = true
                }

                ticketTypeStatuses[ticketTypeId] = {
                    ticketTypeId,
                    isIncreaseDisabled,
                    isDecreaseDisabled,
                    ticketTypeStatusMessage: msg,
                    ticketTypeStatusColor: color,
                }
            })
        } else {
            groupTicketsList.forEach((ticket) => {
                const ticketTypeId = ticket.type_id
                const currentTicketCountForThisType =
                    ticketTypeCounts[ticketTypeId] || 0
                const isStartWaitlist = isWaitlistTicket(ticket)
                const limitValue = isStartWaitlist
                    ? ticket.wait_list
                    : ticket.limit
                const maxAvailable =
                    ticket.max === 0
                        ? limitValue
                        : Math.min(ticket.max, limitValue!)
                let isIncreaseDisabled =
                    currentTicketCountForThisType >= maxAvailable!
                const isDecreaseDisabled = currentTicketCountForThisType === 0
                if (
                    hasSelectedNormalTicket &&
                    !hasSelectedWaitlistTicket &&
                    isStartWaitlist
                )
                    isIncreaseDisabled = true
                else if (
                    hasSelectedWaitlistTicket &&
                    !hasSelectedNormalTicket &&
                    !isStartWaitlist
                )
                    isIncreaseDisabled = true
                else if (hasSelectedNormalTicket && hasSelectedWaitlistTicket)
                    isIncreaseDisabled = true

                let msg = ""
                let color = ""
                const ticketNameForMessage =
                    ticket.en_title || ticket.ar_title || "This Ticket"

                if (
                    currentTicketCountForThisType > 0 &&
                    currentTicketCountForThisType < ticket.min
                ) {
                    msg = dynamicTranslate("select_min_tickets_for_type", {
                        min: ticket.min,
                        // ticketName: "",
                    })
                    color = "red"
                    isFail = true
                } else if (
                    currentTicketCountForThisType > ticket.max &&
                    ticket.max !== 0
                ) {
                    msg = dynamicTranslate("select_max_tickets_for_type", {
                        max: ticket.max,
                        ticketName: ticketNameForMessage,
                    })
                    color = "red"
                    isFail = true
                } else if (currentTicketCountForThisType > 0) {
                    isPass = true
                }
                ticketTypeStatuses[ticketTypeId] = {
                    ticketTypeId,
                    isIncreaseDisabled,
                    isDecreaseDisabled,
                    ticketTypeStatusMessage: msg,
                    ticketTypeStatusColor: color,
                }
            })
        }
    })

    tickets.forEach((ticket) => {
        if (!ticket.group?._id) {
            const ticketTypeId = ticket.type_id
            // If already processed via a group fallback, don't overwrite unless this adds a new error or it wasn't processed at all
            if (
                !ticketTypeStatuses[ticketTypeId] ||
                !ticketTypeStatuses[ticketTypeId].ticketTypeStatusMessage
            ) {
                const currentTicketCountForThisType =
                    ticketTypeCounts[ticketTypeId] || 0
                const isStartWaitlist = isWaitlistTicket(ticket)
                const limitValue = isStartWaitlist
                    ? ticket.wait_list
                    : ticket.limit
                const maxAvailable =
                    ticket.max === 0
                        ? limitValue
                        : Math.min(ticket.max, limitValue!)
                let isIncreaseDisabled =
                    currentTicketCountForThisType >= maxAvailable!
                const isDecreaseDisabled = currentTicketCountForThisType === 0
                if (
                    hasSelectedNormalTicket &&
                    !hasSelectedWaitlistTicket &&
                    isStartWaitlist
                )
                    isIncreaseDisabled = true
                else if (
                    hasSelectedWaitlistTicket &&
                    !hasSelectedNormalTicket &&
                    !isStartWaitlist
                )
                    isIncreaseDisabled = true
                else if (hasSelectedNormalTicket && hasSelectedWaitlistTicket)
                    isIncreaseDisabled = true

                let msg = ""
                let color = ""
                const ticketNameForMessage =
                    ticket.en_title || ticket.ar_title || "This Ticket"

                if (
                    currentTicketCountForThisType > 0 &&
                    currentTicketCountForThisType < ticket.min
                ) {
                    msg = dynamicTranslate("select_min_tickets_for_type", {
                        min: ticket.min,
                        ticketName: ticketNameForMessage,
                    })
                    color = "red"
                    isFail = true
                } else if (
                    currentTicketCountForThisType > ticket.max &&
                    ticket.max !== 0
                ) {
                    msg = dynamicTranslate("select_max_tickets_for_type", {
                        max: ticket.max,
                        ticketName: ticketNameForMessage,
                    })
                    color = "red"
                    isFail = true
                } else if (currentTicketCountForThisType > 0) {
                    isPass = true
                }

                if (ticketTypeStatuses[ticketTypeId]) {
                    // It was processed by group fallback, update if new error
                    if (
                        msg &&
                        !ticketTypeStatuses[ticketTypeId]
                            .ticketTypeStatusMessage
                    ) {
                        ticketTypeStatuses[
                            ticketTypeId
                        ].ticketTypeStatusMessage = msg
                        ticketTypeStatuses[ticketTypeId].ticketTypeStatusColor =
                            color
                    }
                    // Keep existing isIncreaseDisabled/isDecreaseDisabled from group fallback processing
                    ticketTypeStatuses[ticketTypeId].isIncreaseDisabled =
                        isIncreaseDisabled
                    ticketTypeStatuses[ticketTypeId].isDecreaseDisabled =
                        isDecreaseDisabled
                } else {
                    // Not processed by group fallback at all
                    ticketTypeStatuses[ticketTypeId] = {
                        ticketTypeId,
                        isIncreaseDisabled,
                        isDecreaseDisabled,
                        ticketTypeStatusMessage: msg,
                        ticketTypeStatusColor: color,
                    }
                }
            }
        }
    })

    if (selectedTickets.length === 0) {
        isFail = true
        isPass = false
        overallFailureReasonMessage = dynamicTranslate(
            "select_at_least_one_ticket",
            {}
        )
    }

    if (isFail) {
        isPass = false
        if (!overallFailureReasonMessage) {
            // Only set if not already set by "no tickets selected"
            const groupError = Object.values(groupStatuses).find(
                (gs) => gs.groupStatusMessage
            )
            if (groupError) {
                overallFailureReasonMessage = groupError.groupStatusMessage
            } else {
                const ticketError = Object.values(ticketTypeStatuses).find(
                    (ts) => ts.ticketTypeStatusMessage
                )
                if (ticketError) {
                    overallFailureReasonMessage =
                        ticketError.ticketTypeStatusMessage
                } else {
                    overallFailureReasonMessage = dynamicTranslate(
                        "ticket_selection_invalid",
                        {}
                    ) // More generic than unknown
                }
            }
        }
    }

    return {
        ticketTypeStatuses,
        groupStatuses,
        isPass,
        isFail,
        failureReasonMessage: overallFailureReasonMessage,
    }
}

export const groupTickets = (arr: any[]) => {
    const res: any = {}
    arr.forEach((obj: any) => {
        const key = `${obj.en_title}`
        if (!res[key]) {
            res[key] = { ...obj, quantity: 0 }
        }
        res[key].quantity += 1
    })
    return Object.values(res)
}

export function filteredGroupTicketsData(tickets: any[]) {
    const filteredTickets = tickets
        .map((item: any) => {
            if (item.dayNumber === "") {
                return undefined
            } else {
                return item
            }
        })
        .filter((item: any) => item !== undefined)
    return filteredTickets
}
